// File: frontend/src/components/STIX/BundleFilters.tsx
import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  InputAdornment,
  alpha,
  styled
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { BundleFilters as BundleFiltersType } from '../../types/stix';
import { cyber } from '../../theme';

interface BundleFiltersProps {
  onFilterChange: (filters: BundleFiltersType) => void;
}

const SearchCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(cyber.primary, 0.2)}`,
  marginBottom: theme.spacing(3),
}));

const FilterChip = styled(Chip)<{ selected?: boolean }>(({ theme, selected }) => ({
  borderRadius: theme.spacing(1),
  fontWeight: selected ? 'bold' : 'normal',
  transition: 'all 0.2s ease-in-out',
  ...(!selected && {
    borderColor: alpha(cyber.accent.cyan, 0.35),
  }),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: cyber.glow,
  }
}));

const OBJECT_TYPES = [
  { key: 'indicator', label: 'Indicators', icon: '🛡️' },
  { key: 'malware', label: 'Malware', icon: '🦠' },
  { key: 'vulnerability', label: 'CVEs', icon: '🐛' },
  { key: 'attack-pattern', label: 'Attack Patterns', icon: '🎯' },
  { key: 'threat-actor', label: 'Threat Actors', icon: '👤' },
  { key: 'relationship', label: 'Relationships', icon: '🔗' }
];

export const BundleFilters: React.FC<BundleFiltersProps> = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    onFilterChange({
      searchTerm: value,
      dateRange: { start: null, end: null },
      selectedObjectTypes: selectedTypes
    });
  };

  const handleTypeToggle = (typeKey: string) => {
    const newSelectedTypes = selectedTypes.includes(typeKey)
      ? selectedTypes.filter(t => t !== typeKey)
      : [...selectedTypes, typeKey];

    setSelectedTypes(newSelectedTypes);
    onFilterChange({
      searchTerm,
      dateRange: { start: null, end: null },
      selectedObjectTypes: newSelectedTypes
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedTypes([]);
    onFilterChange({
      searchTerm: '',
      dateRange: { start: null, end: null },
      selectedObjectTypes: []
    });
  };

  return (
    <SearchCard elevation={2}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        🔍 Search & Filters
      </Typography>

      {/* Search Field */}
      <TextField
        fullWidth
        placeholder="Search by report title, article ID, or bundle ID..."
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: cyber.accent.cyan }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Object Type Filters */}
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Filter by object types:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {OBJECT_TYPES.map((type) => (
            <FilterChip
              key={type.key}
              label={`${type.icon} ${type.label}`}
              onClick={() => handleTypeToggle(type.key)}
              color={selectedTypes.includes(type.key) ? 'primary' : 'default'}
              variant={selectedTypes.includes(type.key) ? 'filled' : 'outlined'}
              selected={selectedTypes.includes(type.key)}
            />
          ))}
        </Stack>

        {/* Clear Filters */}
        {(searchTerm || selectedTypes.length > 0) && (
          <Box mt={2}>
            <Chip
              label="Clear All Filters"
              onClick={handleClearFilters}
              onDelete={handleClearFilters}
              color="secondary"
              size="small"
            />
          </Box>
        )}
      </Box>
    </SearchCard>
  );
};

export default BundleFilters;
